"use client";

/**
 * AssistantChatDemo — a simulated "AI Assistant" chat card for the marketing site.
 *
 * Animates a three-phase sequence (command → typing indicator → AI reply) and
 * auto-plays when it becomes the active panel (`active` transitions to true).
 * Clicking (or pressing Enter/Space on) the card replays the sequence regardless
 * of active state. When `active` goes false the card keeps its last phase, so a
 * panel scrolled past stays "done". Honors prefers-reduced-motion by jumping
 * straight to the final reply.
 *
 * IMPORTANT: this component emits markup ONLY. Every className here is defined
 * globally (styled-jsx, scoped under `.companion`) by the parent AssistantSection.
 * Do not add any CSS/styles in this file.
 */

import { useCallback, useEffect, useRef, useState } from "react";

type Phase = "cmd" | "typing" | "ai";

export type AssistantStep = {
  node: string;
  meta: string;
  ctx: string;
  slash: string;
  cmd: string;
  tag: string;
  ai: string;
};

export function AssistantChatDemo({
  step,
  active,
  lang,
}: {
  step: AssistantStep;
  active: boolean;
  lang: "en" | "sl";
}) {
  const assistantName = lang === "en" ? "AI Assistant" : "AI pomočnik";

  const [phase, setPhase] = useState<Phase>("cmd");
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = useCallback(() => {
    timers.current.forEach((t) => clearTimeout(t));
    timers.current = [];
  }, []);

  const run = useCallback(() => {
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduced) {
      setPhase("ai");
      return;
    }

    setPhase("cmd");
    timers.current.push(setTimeout(() => setPhase("typing"), 850));
    timers.current.push(setTimeout(() => setPhase("ai"), 2050));
  }, []);

  const replay = useCallback(() => {
    clearTimers();
    run();
  }, [clearTimers, run]);

  useEffect(() => {
    if (active) {
      run();
    }
  }, [active, run]);

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  return (
    <div
      className="cc"
      onClick={replay}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          replay();
        }
      }}
      aria-label="Replay demo"
    >
      <div className="cc-top">
        <span className="cc-dot" />
        <span className="cc-name">{assistantName}</span>
        <span className="cc-live">
          <span className="lv" />
          LIVE
        </span>
      </div>
      <div className="cc-ctx">
        <span className="pt" />
        {step.ctx}
      </div>
      <div className="cc-body">
        <div className="bub you">
          {step.slash ? <span className="sl">{step.slash}</span> : null}
          {step.cmd}
        </div>
        <div className="resp">
          <div className={"typing" + (phase === "typing" ? " show" : "")}>
            <i />
            <i />
            <i />
          </div>
          <div className={"bub ai" + (phase === "ai" ? " show" : "")}>
            <span className="tag">
              <span className="ck" />
              {step.tag}
            </span>
            {step.ai}
          </div>
        </div>
      </div>
    </div>
  );
}
